import React, { useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import StoreCard from './StoreCard';
import AllocationSummaryPanel from './AllocationSummaryPanel';
import BatchPublishProgressBar from './BatchPublishProgressBar';
import { GalleryViewProps } from './types';
import { StoreLoadedData } from './types';

interface GalleryViewInternalProps extends GalleryViewProps {
  sourceStoreId: string;
  date: number;
  department: string;
  onDataLoaded: (storeId: string, data: StoreLoadedData) => void;
}

const GalleryView: React.FC<GalleryViewInternalProps> = ({
  targetStores,
  sourceAllocations,
  sourceStoreId,
  date,
  department,
  onStoreClick,
  onRemoveStore,
  onApplyAll,
  onRetryFailed,
  onDataLoaded,
  batchPublishState,
}) => {
  const allReady = targetStores.length > 0 && targetStores.every((s) => s.readinessStatus === 'ready');
  const needsSequenceCount = targetStores.filter((s) => s.readinessStatus === 'needs-sequence').length;
  const hasFailed = targetStores.some((s) => s.publishStatus === 'failed' || s.publishStatus === 'network-error');
  const batchComplete = batchPublishState.totalCount > 0 && !batchPublishState.isPublishing;
  const [expandedStoreId, setExpandedStoreId] = useState<string | null>(null);

  const handleCardClick = (storeId: string) => {
    const store = targetStores.find((s) => s.storeId === storeId);
    if (store?.readinessStatus === 'ready') {
      // Toggle expand/collapse for ready stores
      setExpandedStoreId((prev) => (prev === storeId ? null : storeId));
    } else {
      // Navigate to sequence drawing for needs-sequence stores
      onStoreClick(storeId);
    }
  };

  const handleRemoveStore = (storeId: string) => {
    // Clear expanded view if the removed store was expanded
    if (expandedStoreId === storeId) {
      setExpandedStoreId(null);
    }
    onRemoveStore(storeId);
  };

  return (
    <Column spacing="400" width="100%" height="100%">
      <Row alignmentHorizontal="justify" alignmentVertical="center" spacingInset="400 400 0 400">
        <Heading level={4}>
          {expandedStoreId ? `Store ${expandedStoreId}` : `Target Stores (${targetStores.length})`}
        </Heading>
        <Row spacing="300">
          {expandedStoreId && (
            <Button type="secondary" onClick={() => setExpandedStoreId(null)}>
              ← Back to Gallery
            </Button>
          )}
          {expandedStoreId && (
            <Button
              type="tertiary"
              onClick={() => {
                handleRemoveStore(expandedStoreId);
              }}
            >
              Remove Store
            </Button>
          )}
          {!expandedStoreId && hasFailed && batchComplete && (
            <Button type="secondary" onClick={onRetryFailed}>
              Retry Failed
            </Button>
          )}
          {!expandedStoreId && (
            <Button type="primary" onClick={onApplyAll} disabled={!allReady || batchPublishState.isPublishing}>
              Apply to All
            </Button>
          )}
        </Row>
      </Row>

      {/* Expanded view — only the selected store, full height */}
      {expandedStoreId &&
        targetStores
          .filter((s) => s.storeId === expandedStoreId)
          .map((store) => (
            <div key={`expanded-${store.storeId}`} style={{ flex: 1, padding: '0 16px', minHeight: 0 }}>
              <StoreCard
                storeId={store.storeId}
                date={date}
                department={department}
                sourceAllocations={sourceAllocations}
                onDataLoaded={onDataLoaded}
                onClick={() => handleCardClick(store.storeId)}
                publishStatus={store.publishStatus}
                publishError={store.publishError}
                expanded={true}
                onRemove={handleRemoveStore}
              />
            </div>
          ))}

      {/* Gallery view — all cards in a grid */}
      {!expandedStoreId && (
        <>
          {!allReady && needsSequenceCount > 0 && (
            <Row spacingInset="0 400">
              <Text type="b200" color="secondary">
                {needsSequenceCount} store{needsSequenceCount > 1 ? 's' : ''} still need
                {needsSequenceCount === 1 ? 's' : ''} sequences
              </Text>
            </Row>
          )}

          {(batchPublishState.isPublishing || batchComplete) && (
            <Row spacingInset="0 400">
              <BatchPublishProgressBar
                total={batchPublishState.totalCount}
                completed={batchPublishState.completedCount}
                failed={batchPublishState.failedCount}
                inProgress={batchPublishState.isPublishing}
              />
            </Row>
          )}

          <Row spacingInset="0 400" wrap="down" spacing="400">
            <Column width={280}>
              <AllocationSummaryPanel allocations={sourceAllocations} sourceStoreId={sourceStoreId} />
            </Column>

            {targetStores.map((store) => (
              <Column key={store.storeId} width={280}>
                <StoreCard
                  storeId={store.storeId}
                  date={date}
                  department={department}
                  sourceAllocations={sourceAllocations}
                  onDataLoaded={onDataLoaded}
                  onClick={() => handleCardClick(store.storeId)}
                  publishStatus={store.publishStatus}
                  publishError={store.publishError}
                  expanded={false}
                  onRemove={handleRemoveStore}
                />
              </Column>
            ))}
          </Row>
        </>
      )}
    </Column>
  );
};

export default GalleryView;
